/**
 * Group Play Dialog Component
 * Multi-mode dialog for joining an adventure with a group.
 * Three modes: With Group, Public Session, Invite Only
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Slider,
  Typography,
  Box,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Avatar,
} from '@mui/material'
import {
  Close as CloseIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { addDays } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import { profileApi } from '@api/endpoints/profile'
import { useCreateGroupSession } from '../hooks/useSession'
import { usePublicSessions, useJoinPublicSession } from '../hooks/useAdventures'
import type { PublicSessionCreatorDto } from '@api/types/session'
import { useAuthStore } from '@store/authStore'
import { useCachedTags } from '@hooks/useCachedTags'
import { TagType } from '../types/adventure-editor.types'
import type { TagCategoryDto } from '../types/adventure-editor.types'
import { AdventureDto, AdventureJoinResponse } from '@api/types/adventure'
import { SessionVisibility } from '@api/types/session'
import { containsProfanity } from '@utils/profanityFilter'

interface GroupPlayDialogProps {
  open: boolean
  onClose: () => void
  adventure: AdventureDto
  onSuccess?: (response: AdventureJoinResponse) => void
}

const FREE_USER_MAX_MEMBERS = 5
const PAID_USER_MAX_MEMBERS = 10

type PlayMode = 'browse' | 'group' | 'public' | 'invite'

function getCreatorDisplay(creator: PublicSessionCreatorDto | null | undefined): { name: string; avatarUrl: string | null } {
  if (!creator || !creator.entityName) return { name: 'Anonymous', avatarUrl: null }
  return { name: creator.entityName, avatarUrl: creator.entityAvatarUrl }
}

/**
 * Countdown display for session start time.
 * Shows "DDd HH:MM:SS" until start. Returns null if start time is in the past.
 * Treats startDateTime as UTC.
 */
function StartCountdown({ startDateTime }: { startDateTime: string }) {
  const [remaining, setRemaining] = useState<number>(() => {
    const target = new Date(startDateTime + 'Z').getTime()
    return Math.max(0, target - Date.now())
  })

  useEffect(() => {
    const target = new Date(startDateTime + 'Z').getTime()
    const update = () => setRemaining(Math.max(0, target - Date.now()))
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [startDateTime])

  if (remaining <= 0) return null

  const totalSec = Math.floor(remaining / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60

  const pad = (n: number) => n.toString().padStart(2, '0')
  const label = days > 0
    ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TimerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {label}
      </Typography>
    </Box>
  )
}

/** Chip list for a single subcategory's tags */
function TagChipList({
  tags,
  selectedIds,
  onToggle,
}: {
  tags: TagCategoryDto['tags']
  selectedIds: string[]
  onToggle: (tagId: string) => void
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {(tags || []).map(tag => {
        const isSelected = selectedIds.includes(tag.id)
        return (
          <Chip
            key={tag.id}
            label={tag.displayName || tag.name}
            size="small"
            onClick={() => onToggle(tag.id)}
            color={isSelected ? 'primary' : 'default'}
            variant={isSelected ? 'filled' : 'outlined'}
            sx={{
              cursor: 'pointer',
              ...(tag.color && !isSelected ? { borderColor: tag.color } : {}),
            }}
          />
        )
      })}
    </Box>
  )
}

/** Accordion for a tag category group (Gender, Interests, Looking For) */
function TagCategoryAccordion({
  label,
  categories,
  selectedIds,
  onToggle,
  getTag,
}: {
  label: string
  categories: TagCategoryDto[]
  selectedIds: string[]
  onToggle: (tagId: string) => void
  getTag: (tagId: string) => any
}) {
  const allTags = categories.flatMap(c => c.tags || [])
  if (allTags.length === 0) return null

  const selectedCount = selectedIds.length
  const hasSubcategories = categories.length > 1

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' }, mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
          {selectedCount > 0 && (
            <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
              ({selectedCount} selected)
            </Typography>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: hasSubcategories ? 0 : 2, pb: hasSubcategories ? 0 : 1 }}>
        {hasSubcategories ? (
          categories.map(category => {
            const catTags = category.tags || []
            if (catTags.length === 0) return null
            const catSelectedCount = catTags.filter(t => selectedIds.includes(t.id)).length
            return (
              <Accordion
                key={category.id}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, px: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {category.displayName || category.name}
                    {catSelectedCount > 0 && (
                      <Typography component="span" variant="caption" color="primary" sx={{ ml: 0.5 }}>
                        ({catSelectedCount})
                      </Typography>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 2, pb: 1 }}>
                  <TagChipList tags={catTags} selectedIds={selectedIds} onToggle={onToggle} />
                </AccordionDetails>
              </Accordion>
            )
          })
        ) : (
          <TagChipList tags={allTags} selectedIds={selectedIds} onToggle={onToggle} />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export function GroupPlayDialog({
  open,
  onClose,
  adventure,
  onSuccess,
}: GroupPlayDialogProps) {
  const { user } = useAuthStore()
  const createGroupSession = useCreateGroupSession()
  const { getCategoriesByType, getTag } = useCachedTags()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [mode, setMode] = useState<PlayMode>('browse')

  // Browse tab hooks
  const { data: publicSessions, isLoading: isLoadingSessions } = usePublicSessions(adventure.id, open && mode === 'browse')
  const joinSessionMutation = useJoinPublicSession()

  // Shared form state
  const [startDateTime, setStartDateTime] = useState<Date | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // "With Group" mode state
  const [selectedGroupId, setSelectedGroupId] = useState('')

  // "Public" and "Invite Only" mode state
  const [sessionName, setSessionName] = useState('')
  const [sessionDescription, setSessionDescription] = useState('')
  const [maxParticipants, setMaxParticipants] = useState(5)

  // Tag selection state
  const [selectedGenderTagIds, setSelectedGenderTagIds] = useState<string[]>([])
  const [selectedInterestTagIds, setSelectedInterestTagIds] = useState<string[]>([])
  const [selectedLookingForTagIds, setSelectedLookingForTagIds] = useState<string[]>([])
  const [requireTagMatch, setRequireTagMatch] = useState(false)

  const isPaidUser = user?.isPaidSubscription === true
  const maxMemberLimit = isPaidUser ? PAID_USER_MAX_MEMBERS : FREE_USER_MAX_MEMBERS

  // Fetch user's groups for "With Group" mode
  const { data: myGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['my-groups'],
    queryFn: () => socialApi.getMyGroups(),
    enabled: open && mode === 'group',
  })

  // Prefetch user's profile for tag validation on submit (non-blocking)
  const { data: myProfile } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => profileApi.getMyProfile(),
    enabled: open,
    staleTime: 300000,
  })

  // Get tag categories by type
  const genderCategories = getCategoriesByType(TagType.GenderIdentity)
  const interestCategories = getCategoriesByType(TagType.Interests)
  const lookingForCategories = getCategoriesByType(TagType.LookingFor)

  const isPending = createGroupSession.isPending

  // Silent validation: check that for each tag group the user selected,
  // their profile contains at least one of the selected tags
  const validateTagsAgainstProfile = (): boolean => {
    if (!myProfile) return true // No profile loaded, let backend handle it

    const profileGenderIds = [myProfile.genderIdentityTagId, myProfile.genderIdentity].filter(Boolean) as string[]
    const profileInterestIds = [...(myProfile.interestTagIds || []), ...(myProfile.interestTags || [])]
    const profileLookingForIds = [...(myProfile.lookingForTagIds || []), ...(myProfile.lookingFor || [])]

    if (selectedGenderTagIds.length > 0 && !selectedGenderTagIds.some(id => profileGenderIds.includes(id))) {
      return false
    }
    if (selectedInterestTagIds.length > 0 && !selectedInterestTagIds.some(id => profileInterestIds.includes(id))) {
      return false
    }
    if (selectedLookingForTagIds.length > 0 && !selectedLookingForTagIds.some(id => profileLookingForIds.includes(id))) {
      return false
    }
    return true
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (mode === 'group') {
      if (!selectedGroupId) {
        newErrors.group = 'Please select a group'
      }
    } else {
      if (!sessionName.trim()) {
        newErrors.sessionName = 'Session name is required'
      } else if (sessionName.length < 3) {
        newErrors.sessionName = 'Session name must be at least 3 characters'
      } else if (containsProfanity(sessionName)) {
        newErrors.sessionName = 'Session name contains inappropriate language. Please choose another name.'
      }
    }

    if (startDateTime) {
      const now = new Date()
      if (startDateTime <= now) {
        newErrors.startDateTime = 'Start time must be in the future'
      } else if (startDateTime > addDays(now, 30)) {
        newErrors.startDateTime = 'Start time must be within 30 days'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    // Silent tag validation — generic error if tags don't match profile
    if ((mode === 'public' || mode === 'invite') && !validateTagsAgainstProfile()) {
      setErrors({ submit: 'Failed to create session' })
      return
    }

    try {
      // Determine visibility based on mode
      const visibility = mode === 'public'
        ? SessionVisibility.Public
        : SessionVisibility.InviteOnly // Both "group" and "invite" modes create invite-only sessions

      // Build tag JSON strings (only for public/invite modes with selections)
      const genderTagsJson = selectedGenderTagIds.length > 0
        ? JSON.stringify(selectedGenderTagIds) : undefined
      const interestTagsJson = selectedInterestTagIds.length > 0
        ? JSON.stringify(selectedInterestTagIds) : undefined
      const lookingForTagsJson = selectedLookingForTagIds.length > 0
        ? JSON.stringify(selectedLookingForTagIds) : undefined

      const result = await createGroupSession.mutateAsync({
        adventureId: adventure.id,
        sessionName: mode === 'group'
          ? `${adventure.title} - Group Session`
          : sessionName.trim(),
        visibility,
        maxParticipants: mode === 'group' ? maxMemberLimit : maxParticipants,
        groupId: mode === 'group' ? selectedGroupId : undefined,
        startDateTime: startDateTime?.toISOString() ?? undefined,
        requireTagMatch: (mode === 'public' || mode === 'invite') ? requireTagMatch : false,
        genderTagsJson: mode !== 'group' ? genderTagsJson : undefined,
        interestTagsJson: mode !== 'group' ? interestTagsJson : undefined,
        lookingForTagsJson: mode !== 'group' ? lookingForTagsJson : undefined,
      })

      // Response is AdventureSessionFullDto returned directly (not wrapped in ApiResponse)
      if (result?.id) {
        const joinResponse: AdventureJoinResponse = {
          success: true,
          sessionId: result.id,
        }
        onSuccess?.(joinResponse)
        handleClose()
      } else {
        setErrors({
          submit: 'Failed to create session — no data in response',
        })
      }
    } catch (err: any) {
      // Try to extract error message from API response
      const message = err?.response?.data?.message
        || err?.response?.data?.errors?.join(', ')
        || (err instanceof Error ? err.message : 'Failed to create session')
      setErrors({ submit: message })
    }
  }

  const toggleTag = (tagId: string, selected: string[], setSelected: (ids: string[]) => void) => {
    setSelected(
      selected.includes(tagId)
        ? selected.filter(id => id !== tagId)
        : [...selected, tagId]
    )
  }

  const handleJoinSession = async (sessionId: string) => {
    try {
      const response = await joinSessionMutation.mutateAsync(sessionId)
      if (response.success) {
        onSuccess?.(response)
        handleClose()
      }
    } catch {
      // Error handled by mutation state
    }
  }

  const handleClose = () => {
    setMode('browse')
    setStartDateTime(null)
    setSelectedGroupId('')
    setSessionName('')
    setSessionDescription('')
    setMaxParticipants(5)
    setSelectedGenderTagIds([])
    setSelectedInterestTagIds([])
    setSelectedLookingForTagIds([])
    setRequireTagMatch(false)
    setErrors({})
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon />
            <Typography variant="h6">Group Play</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Error Alert */}
          {errors.submit && (
            <Alert severity="error">{errors.submit}</Alert>
          )}

          {/* Mode Tabs */}
          <Tabs
            value={mode}
            onChange={(_, v) => { setMode(v); setErrors({}) }}
            variant="fullWidth"
          >
            <Tab icon={<PeopleIcon />} label="Browse" value="browse" />
            <Tab icon={<GroupIcon />} label="With Group" value="group" />
            <Tab icon={<PublicIcon />} label="Public" value="public" />
            <Tab icon={<LockIcon />} label="Invite Only" value="invite" />
          </Tabs>

          {/* Browse Sessions Mode */}
          {mode === 'browse' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {isLoadingSessions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (() => {
                const sessionList = Array.isArray(publicSessions) ? publicSessions : []
                return sessionList.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      No public sessions available right now.
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                      Create your own public session to get started!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {sessionList.map((session) => (
                      <Card key={session.sessionId} variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {session.sessionName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {(() => {
                                    const { name, avatarUrl } = getCreatorDisplay(session.creator)
                                    return (
                                      <>
                                        <Avatar
                                          src={avatarUrl || undefined}
                                          sx={{ width: 16, height: 16, fontSize: '0.6rem' }}
                                        >
                                          {name[0]}
                                        </Avatar>
                                        <Typography variant="caption" color="text.secondary">
                                          {name}
                                        </Typography>
                                      </>
                                    )
                                  })()}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {session.memberCount}/{session.maxParticipants}
                                  </Typography>
                                </Box>
                                {session.startDateTime && (
                                  <StartCountdown startDateTime={session.startDateTime} />
                                )}
                              </Box>
                              {[...session.genderTags, ...session.interestTags, ...session.lookingForTags].length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                  {[...session.genderTags, ...session.interestTags, ...session.lookingForTags].slice(0, 3).map((tag) => (
                                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                                  ))}
                                </Box>
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleJoinSession(session.sessionId)}
                              disabled={joinSessionMutation.isPending || session.memberCount >= session.maxParticipants}
                            >
                              {session.memberCount >= session.maxParticipants ? 'Full' : 'Join'}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}

                    {joinSessionMutation.isError && (
                      <Typography color="error" variant="body2">
                        {joinSessionMutation.error instanceof Error ? joinSessionMutation.error.message : 'Failed to join session'}
                      </Typography>
                    )}
                  </>
                )
              })()}
            </Box>
          )}

          {/* With Group Mode */}
          {mode === 'group' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select one of your existing groups to play this adventure together.
                A new session will be created and group members can be invited to join.
              </Typography>
              {isLoadingGroups ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl fullWidth error={!!errors.group}>
                  <InputLabel>Select Group</InputLabel>
                  <Select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    label="Select Group"
                  >
                    {myGroups && Array.isArray(myGroups) && myGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.group && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.group}
                    </Typography>
                  )}
                  {(!myGroups || (Array.isArray(myGroups) && myGroups.length === 0)) && !isLoadingGroups && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      You don't have any groups yet. Create one from the Social Hub.
                    </Typography>
                  )}
                </FormControl>
              )}
            </Box>
          )}

          {/* Public Session / Invite Only Mode */}
          {(mode === 'public' || mode === 'invite') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {mode === 'public'
                  ? 'Create a public session that anyone can join.'
                  : 'Create an invite-only session. You can invite friends after creating it.'
                }
              </Typography>

              <TextField
                label="Session Name"
                required
                fullWidth
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                error={!!errors.sessionName}
                helperText={errors.sessionName || `This name will be displayed on leaderboards. ${sessionName.length}/100`}
                inputProps={{ maxLength: 100 }}
              />

              <TextField
                label="Description (optional)"
                fullWidth
                multiline
                rows={2}
                value={sessionDescription}
                onChange={(e) => setSessionDescription(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />

              <Box>
                <Typography variant="body2" gutterBottom>
                  Max Participants: {maxParticipants}
                </Typography>
                <Slider
                  value={maxParticipants}
                  onChange={(_, value) => setMaxParticipants(value as number)}
                  min={2}
                  max={maxMemberLimit}
                  marks={[
                    { value: 2, label: '2' },
                    { value: maxMemberLimit, label: `${maxMemberLimit}` },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              {/* Tag Preferences — only for public sessions (tags help with discoverability) */}
              {mode === 'public' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Session Preferences
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Optionally select tags to help others find your session.
                  </Typography>

                  {/* Gender Tags */}
                  <TagCategoryAccordion
                    label="Gender Identity"
                    categories={genderCategories}
                    selectedIds={selectedGenderTagIds}
                    onToggle={(id) => toggleTag(id, selectedGenderTagIds, setSelectedGenderTagIds)}
                    getTag={getTag}
                  />

                  {/* Interest Tags */}
                  <TagCategoryAccordion
                    label="Interests"
                    categories={interestCategories}
                    selectedIds={selectedInterestTagIds}
                    onToggle={(id) => toggleTag(id, selectedInterestTagIds, setSelectedInterestTagIds)}
                    getTag={getTag}
                  />

                  {/* Looking For Tags */}
                  <TagCategoryAccordion
                    label="Looking For"
                    categories={lookingForCategories}
                    selectedIds={selectedLookingForTagIds}
                    onToggle={(id) => toggleTag(id, selectedLookingForTagIds, setSelectedLookingForTagIds)}
                    getTag={getTag}
                  />

                  {(selectedGenderTagIds.length > 0 || selectedInterestTagIds.length > 0 || selectedLookingForTagIds.length > 0) && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={requireTagMatch}
                          onChange={(e) => setRequireTagMatch(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Require tag match to join
                        </Typography>
                      }
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Start Time (shared across create modes, not browse) */}
          {mode !== 'browse' && (
            <Box>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Start Time (optional)"
                  value={startDateTime}
                  onChange={(v) => setStartDateTime(v)}
                  minDateTime={new Date()}
                  maxDateTime={addDays(new Date(), 30)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.startDateTime,
                      helperText: errors.startDateTime || 'Leave empty to start immediately',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          )}
        </Box>
      </DialogContent>

      {mode !== 'browse' && (
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isPending}
            startIcon={mode === 'group' ? <GroupIcon /> : mode === 'public' ? <PublicIcon /> : <LockIcon />}
          >
            {isPending ? 'Creating...' : mode === 'group' ? 'Join with Group' : 'Create Session'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
